import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { seo } from "../translations/seo";
import api from "../api/client";
import { useLanguage } from "../context/LanguageContext";

export default function Centres() {
  const { t, language } = useLanguage();
  const [centres, setCentres] = useState([]);

  useEffect(() => {
    api.get("/centres").then((r) => setCentres(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white font-jakarta">
      <Navbar />
      <Seo title={seo[language].centres.title} description={seo[language].centres.description} />

      {/* Hero */}
      <section className="pt-32 pb-14 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#2E4DB5] mb-4">
            {t.centres.eyebrow}
          </p>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-5">
            {t.centres.title}
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            {t.centres.subtitle}
          </p>
        </div>
      </section>

      {/* Cards */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 grid md:grid-cols-2 gap-8">
          {centres.map((c) => {
            const l = c.i18n?.[language] || c.i18n?.fr || {};
            return (
              <Link
                key={c.slug}
                to={`/centres/${c.slug}`}
                className="group bg-white rounded-3xl border border-slate-100 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
                data-testid={`centre-card-${c.slug}`}
              >
                <div className="h-64 overflow-hidden bg-slate-100">
                  {c.photo ? (
                    <img
                      src={c.photo}
                      alt={c.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full gradient-bg opacity-30 flex items-center justify-center">
                      <MapPin className="w-16 h-16 text-white" />
                    </div>
                  )}
                </div>
                <div className="p-8 flex flex-col flex-1">
                  <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                    {c.name}
                  </h2>
                  <p className="text-slate-500 text-sm mb-4">{l.tagline}</p>
                  <div className="flex items-start gap-2 text-slate-600 text-sm mb-6">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#3B9DA5]" />
                    <span>
                      {c.address_line}
                      <br />
                      {c.postal_code} {c.city}
                    </span>
                  </div>
                  {l.features && (
                    <ul className="space-y-2 mb-6 flex-1">
                      {l.features.slice(0, 4).map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-[#3B9DA5] flex-shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between text-[#2E4DB5] font-semibold text-sm group-hover:gap-3 gap-2 transition-all">
                    <span>{t.centres.discover}</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
}
